import { provideHttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject, isDevMode } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { 
    PreloadAllModules, 
    provideRouter, 
    withInMemoryScrolling, 
    withPreloading,
    withRouterConfig
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from './app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { provideGci } from '@gci';
import { webApiServices } from 'app/web-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { AuthService } from './core/auth/auth.service';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, FALLBACK_LANGUAGE } from './models/language.constants';
import { environment } from 'environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { provideMarkdown } from 'ngx-markdown';

export function initializeApp(authService: AuthService) {
    return () => authService.loadSession();
  }

export const appConfig: ApplicationConfig = {
    providers: [
        provideMarkdown(),
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [AuthService],
            multi: true
        },
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.production || environment.serviceWorker,
            registrationStrategy: 'registerWhenStable:30000'
        }),
        provideAnimations(),
        provideHttpClient(),
        // provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            appRoutes, 
            withPreloading(PreloadAllModules), 
            withInMemoryScrolling({ 
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled'
            })
        ),
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: AVAILABLE_LANGUAGES,
                defaultLang: DEFAULT_LANGUAGE,
                fallbackLang: FALLBACK_LANGUAGE,
                reRenderOnLangChange: true,
                prodMode: environment.production,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideGci({
            webApi: {
                delay: 0,
                services: webApiServices
            }
        }),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'dense',
                scheme: 'auto',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                    '2xl': '1600px',
                },
                theme: environment.theme,
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                    {
                        id: 'theme-esyasoft',
                        name: 'Esyasoft'
                    },
                    {
                        id: 'theme-cbn',
                        name: 'Cbn'
                    }
                ],
            },
        }),
    ],
};
